import type {ITerritoryFeature} from "@esWebAPI/es/map/TerritoryModel";
import type {Feature, FeatureCollection} from "geojson";
import type {TerritoryBaseLayer} from "@esWebAPI/es/map/layer/territory/TerritoryBaseLayer";
import type {TerritoryLayerWorkerBroker} from "@esWebAPI/es/map/layer/territory/TerritoryLayerWorkerBroker";

export class TerritoryOutlinesWorkerBroker {
        private readonly layerMetaId: number;
        private worker: Worker;
        sequenceNumber;
        messagesCallbacks: any;
        layerIndex: number|null;

        constructor(mainLayerMetaId: number) {
            this.sequenceNumber = 0;   // Sequence for messageIds
            this.messagesCallbacks = {}; // Map with key as messageId and value as callback
            this.layerMetaId = mainLayerMetaId;
            this.layerIndex = null;

            try {
                this.worker = new Worker(es.appUrl + "js/esApp/workers/territoryOutlinesLayerWorker.js?v=" + es.ver,
                    {name: "TerritoryOutlinesWorker"});
                this.worker.addEventListener("message", this.onMessage.bind(this));
                this.worker.addEventListener("error", this.onError.bind(this))
            } catch (e) {
                console.error(e);
                throw new Error("Failed to initialize worker");
            }
        }

        destroy () {
           this.worker.terminate();
           this.sequenceNumber = 0;
           this.messagesCallbacks = {};
        }

        onMessage (message: any) {
            const callback = this.messagesCallbacks[message.data.id];
            if(!callback){
                console.error("No callback for message", message.data);
                return;
            }
            callback(message.data.data);
        }

        onError(message: any) {
            console.error("Error in territoryOutlinesLayerWorker", message);
        }

        sendMessage (message: any, callback: any) {
            const messageId = this.getSequenceNumber();
            message.id = messageId;
            this.worker.postMessage(message);
            this.messagesCallbacks[messageId] = callback;
        }

        getSequenceNumber () {
            return ++this.sequenceNumber;
        }

        updateOutlines(actions: any, territories: ITerritoryFeature[], affectedParentsIds: number[], changedTerritoriesIds: number[], allTerritoryFeatures: any) {
            const dfd = $.Deferred();
            let message = {cmd: 'updateOutlines', data: {actions, territories, affectedParentsIds, changedTerritoriesIds, allTerritoryFeatures}};
            this.sendMessage(message, function (r: any){
                dfd.resolve(r);
            })
            return dfd.promise();
        }

        clear(layerMetaId: number) {
            const dfd = $.Deferred();
            const cmd = {
                cmd: "clear",
                data: {
                    layerMetaId
                }
            }
            this.sendMessage(cmd, () => dfd.resolve());
            return dfd;
        }

        getVisualCentersInBounds(bounds: [number, number, number, number], level: number) {
            const dfd = $.Deferred();
            const cmd = {
                cmd: "getVisualCentersInBounds",
                data: {
                    bounds,
                    level
                }
            }

            this.sendMessage(cmd, (visualCenters: any) => dfd.resolve(visualCenters));
            return dfd;
        }

        saveState(stateId: number) {
            const dfd = $.Deferred();
            const cmd = {
                cmd: "saveState",
                data: {
                    stateId
                }
            }
            this.sendMessage(cmd, () => dfd.resolve());
            return dfd;
        }

        loadState(stateId: number) {
            const dfd = $.Deferred();
            const cmd = {
                cmd: "loadState",
                data: {
                    stateId
                }
            }
            this.sendMessage(cmd, () => dfd.resolve());
            return dfd;
        }

         /**
         * Get the features that interact with the specified feature in any way (intersects, contains, within)
         * @param geom
         * @param callback
         */
        anyInteract (geom: any, callback: any) {
            const message = {
                cmd: "anyInteract",
                data: {feature: geom}
            };
            this.sendMessage(message, callback);
        }

        init (baseLayer: TerritoryBaseLayer, territoryFeatures: ITerritoryFeature[], nLevels: number, lastModified: number){
            const dfd = $.Deferred();
            const messageId = this.getSequenceNumber();
            const message = {cmd: 'initOutlines', id: messageId, data:{layerMetaId: this.layerMetaId, territoryFeatures, nLevels, lastModified}};

            const channel = new MessageChannel();
            this.worker.postMessage({cmd: "connectBaseLayerWorker"}, [channel.port1]);
            (baseLayer.featureWorker as TerritoryLayerWorkerBroker).getWorker()?.postMessage({cmd: "connectOutlinesLayerWorker"}, [channel.port2]);

            this.messagesCallbacks[messageId] = dfd.resolve;
            this.worker.postMessage(message);
            return dfd.promise();
        }

        getAll(): JQuery.Promise<FeatureCollection> {
            const dfd = $.Deferred();
            const messageId = this.getSequenceNumber();
            this.messagesCallbacks[messageId] = dfd.resolve;
            const cmd = {cmd: "getAll", id: messageId};
            this.worker.postMessage(cmd);
            return dfd.promise();
        }

        getFeatureById (id: number): Promise<Feature> {
            const messageId = this.getSequenceNumber();

            return new Promise((resolve) => {
                this.messagesCallbacks[messageId] = (rs: Feature) => {
                    resolve(rs);
                };
                const cmd = {
                    cmd: "getFeatureById",
                    id: messageId,
                    data: {
                        id
                    }
                }
                this.worker.postMessage(cmd);
            });
        }

        getVisualCenterFeatures(bounds: [number, number, number, number], level: number|undefined, callback: any) {
            const messageId = this.getSequenceNumber();
            this.messagesCallbacks[messageId] = callback;
            const cmd = {
                cmd: "getVisualCenterFeatures",
                id: messageId,
                data: {bounds, level}
            }
            this.worker.postMessage(cmd);
        }
    }
